import { STREAM_FRAME_RATES, STREAM_PREVIEW_MAX, STREAM_RESOLUTIONS, type StreamFrameRate, type StreamResolution, type StreamSettings } from "@atrium/shared";

/** Go live choices, without `audio` (that's whether the captured surface actually has sound). */
export interface StreamChoice {
  resolution: StreamResolution;
  fps: StreamFrameRate;
  /** Ask the browser for tab or system audio. */
  audio: boolean;
}

const KEY = "atrium:stream:settings";
const DEFAULT: StreamChoice = { resolution: "1080", fps: 30, audio: true };

export function loadStreamChoice(): StreamChoice {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) ?? "null") as Partial<StreamChoice> | null;
    if (!raw) return DEFAULT;
    return {
      resolution: STREAM_RESOLUTIONS.includes(raw.resolution as StreamResolution) ? (raw.resolution as StreamResolution) : DEFAULT.resolution,
      fps: STREAM_FRAME_RATES.includes(raw.fps as StreamFrameRate) ? (raw.fps as StreamFrameRate) : DEFAULT.fps,
      audio: typeof raw.audio === "boolean" ? raw.audio : DEFAULT.audio,
    };
  } catch {
    return DEFAULT;
  }
}

export const saveStreamChoice = (c: StreamChoice) => localStorage.setItem(KEY, JSON.stringify(c));

const HEIGHT: Record<StreamResolution, number | null> = { "720": 720, "1080": 1080, source: null };

export const resolutionLabel = (r: StreamResolution) => (r === "source" ? "Source" : `${r}p`);
export const qualityLabel = (s: Pick<StreamSettings, "resolution" | "fps">) => `${resolutionLabel(s.resolution)} · ${s.fps} fps`;

/** Size and frame rate limits for the captured video track. */
export function videoConstraints(c: Pick<StreamChoice, "resolution" | "fps">): MediaTrackConstraints {
  const h = HEIGHT[c.resolution];
  return {
    ...(h ? { height: { max: h }, width: { max: Math.round((h * 16) / 9) } } : {}),
    frameRate: { ideal: c.fps, max: c.fps },
  };
}

export function displayMediaOptions(c: StreamChoice): DisplayMediaStreamOptions & Record<string, unknown> {
  return {
    video: videoConstraints(c),
    audio: c.audio ? { echoCancellation: false, noiseSuppression: false, autoGainControl: false } : false,
    systemAudio: c.audio ? "include" : "exclude",
    // Lets people switch tabs from the browser's own sharing bar without ending the stream.
    surfaceSwitching: "include",
    selfBrowserSurface: "exclude",
  };
}

/** Text and slides stay sharp; high frame rates (games, video) favour smooth motion instead. */
export const contentHintFor = (fps: StreamFrameRate) => (fps >= 60 ? "motion" : "detail");

/**
 * Upload ceiling for one viewer. The mesh sends a separate copy to each viewer,
 * so the per-viewer budget shrinks once more than a few people are watching.
 */
export function bitrateFor(c: Pick<StreamSettings, "resolution" | "fps">, viewers: number) {
  const base = { "720": 2_000_000, "1080": 3_500_000, source: 5_000_000 }[c.resolution];
  const fps = { 15: 0.6, 30: 1, 60: 1.6 }[c.fps];
  const share = viewers > 3 ? Math.max(0.4, 3 / viewers) : 1;
  return Math.round(base * fps * share);
}

/** A small JPEG of the stream's current frame, for people who aren't watching. */
export async function snapshot(video: HTMLVideoElement): Promise<string | null> {
  if (!video.videoWidth || !video.videoHeight) return null;
  const w = 384;
  const h = Math.round((w * video.videoHeight) / video.videoWidth);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = Math.min(h, 384);
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
  for (const q of [0.7, 0.5, 0.3]) {
    const url = canvas.toDataURL("image/jpeg", q);
    if (url.length <= STREAM_PREVIEW_MAX) return url;
  }
  return null;
}
